import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore, selectIsAuthenticated } from './lib/auth';
import { ErrorBoundary } from './components/ErrorBoundary';

// Pages
import LoginPage       from './pages/LoginPage';
import DashboardPage   from './pages/DashboardPage';
import ProductsPage    from './pages/ProductsPage';
import CategoriesPage  from './pages/CategoriesPage';
import CollectionsPage from './pages/CollectionsPage';
import OrdersPage      from './pages/OrdersPage';
import InventoryPage   from './pages/InventoryPage';
import DiscountsPage   from './pages/DiscountsPage';
import MediaPage       from './pages/MediaPage';
import StaffPage       from './pages/StaffPage';
import SettingsPage    from './pages/SettingsPage';

import ReviewsPage   from './pages/ReviewsPage';
import GiftCardsPage from './pages/GiftCardsPage';
import CustomersPage from './pages/CustomersPage';
import TransfersPage from './pages/TransfersPage';
import JournalPage   from './pages/JournalPage';
import WebhooksPage  from './pages/WebhooksPage';
import ReportsPage   from './pages/ReportsPage';
import ContentPage   from './pages/ContentPage';

// Layout
import AdminLayout     from './components/layout/AdminLayout';

// ── Protected Route ────────────────────────────────────────
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  const { refreshSession, session } = useAuthStore();

  // Refresh session on mount if we have one
  useEffect(() => {
    if (session) refreshSession();
  }, []);

  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard"    element={<DashboardPage />} />
          <Route path="products"     element={<ProductsPage />} />
          <Route path="categories"   element={<CategoriesPage />} />
          <Route path="collections"  element={<CollectionsPage />} />
          <Route path="orders"       element={<OrdersPage />} />
          <Route path="inventory"    element={<InventoryPage />} />
          <Route path="transfers"    element={<TransfersPage />} />
          <Route path="discounts"    element={<DiscountsPage />} />
          <Route path="gift-cards"   element={<GiftCardsPage />} />
          <Route path="customers"    element={<CustomersPage />} />
          <Route path="reviews"      element={<ReviewsPage />} />
          <Route path="journal"      element={<JournalPage />} />
          <Route path="reports"      element={<ReportsPage />} />
          <Route path="webhooks"     element={<WebhooksPage />} />
          <Route path="content"      element={<ContentPage />} />
          <Route path="media"        element={<MediaPage />} />
          <Route path="staff"        element={<StaffPage />} />
          <Route path="settings"     element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}
